import { Component } from '@theme/component';
import { debounce, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const CLOSE_DELAY = 100;
const PREPARE_EVENT = 'seal:header-menu:prepare';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeSubmenu - The submenu owned by the active item.
 * @property {string | null} activeControlId - Stable submenu id used to recover after section hydration.
 * @property {boolean} isDefaultSlot - Whether the active item is in the main menu row.
 * @property {boolean} hasSubmenu - Whether the active item owns its own submenu.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /** @type {ResizeObserver | null} */
  #submenuResizeObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  /** @type {number | undefined} */
  #metricsAnimationFrame;

  /** @type {number | undefined} */
  #structureAnimationFrame;

  #suppressFocusActivation = false;

  #structureObserver = new MutationObserver(() => {
    if (!this.#state.activeItem || !this.#activeStructureNeedsReconcile()) return;
    this.#scheduleStructureReconcile();
  });

  #activationId = 0;

  /** @type {State} */
  #state = {
    activeItem: null,
    activeSubmenu: null,
    activeControlId: null,
    isDefaultSlot: true,
    hasSubmenu: false,
  };

  connectedCallback() {
    super.connectedCallback();

    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('keydown', this.#onKeyDown);
    this.overflowMenu?.addEventListener('pointerenter', this.#overflowSubmenuEnterListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuLeaveListener);
    this.#structureObserver.observe(this, {
      attributes: true,
      attributeFilter: ['aria-expanded', 'data-active', 'slot'],
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.overflowMenu?.removeEventListener('pointerenter', this.#overflowSubmenuEnterListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuLeaveListener);
    this.#structureObserver.disconnect();
    this.#closeImmediately(this.#state.activeItem, this.#activationId, true);
    this.#cancelClose();
    this.#cancelMetricsSync();
    this.#cancelStructureReconcile();
    this.#disconnectResizeObserver();
  }

  updatedCallback() {
    super.updatedCallback();

    if (!this.#state.activeItem) return;

    this.#reconcileActiveStructure();
  }

  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    const activationId = this.#activationId;

    requestAnimationFrame(() => {
      if (activationId !== this.#activationId) return;

      if (!this.#isDesktopMenu()) {
        this.#closeImmediately(this.#state.activeItem, activationId, true);
        return;
      }

      this.#reconcileActiveStructure();
    });
  }, 100);

  #overflowSubmenuEnterListener = () => {
    this.#cancelClose();
  };

  #overflowSubmenuLeaveListener = () => {
    this.#scheduleDeactivate(this.#state.activeItem);
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;

    event.preventDefault();
    const activeItem = this.#state.activeItem;
    this.#suppressFocusActivation = true;
    this.#closeImmediately(activeItem, this.#activationId, true);
    if (document.activeElement !== activeItem) activeItem.focus();
    queueMicrotask(() => {
      this.#suppressFocusActivation = false;
    });
  };

  /**
   * Get the overflow menu.
   * @returns {HTMLElement | null}
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered.
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  /** @returns {HTMLElement | null} */
  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (event.type === 'focus' && this.#suppressFocusActivation) {
      this.#suppressFocusActivation = false;
      return;
    }

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent || !this.#isDesktopMenu()) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    this.#openItem(item);
  };

  close() {
    this.#closeImmediately(this.#state.activeItem, this.#activationId, true);
  }

  /** @param {HTMLElement} item */
  #openItem(item) {
    this.#cancelClose();

    const isDefaultSlot = item.parentElement?.slot === '';
    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const previousItem = this.#state.activeItem;
    const previousSubmenu = this.#state.activeSubmenu;
    const activationId = ++this.#activationId;

    this.#cancelMetricsSync();
    this.#disconnectResizeObserver();

    if (previousItem && previousItem !== item) {
      previousItem.ariaExpanded = 'false';
    }

    if (previousSubmenu && previousSubmenu !== submenu) {
      delete previousSubmenu.dataset.active;
    }

    this.#state = {
      activeItem: item,
      activeSubmenu: submenu,
      activeControlId: item.getAttribute('aria-controls'),
      isDefaultSlot,
      hasSubmenu,
    };

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    if (submenu && !submenu.hasAttribute('data-active')) {
      submenu.dataset.active = '';
    }

    this.#prepareActiveSubmenu();
    this.#syncActiveSubmenuMetrics();
    this.#observeActiveSubmenu(submenu, activationId);
    this.#scheduleMetricsSync(activationId);
  }

  /**
   * Deactivate the item that emitted the leave or blur event after a short grace period.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item || item !== this.#state.activeItem) return;

    const itemContainer = item.parentElement;
    const relatedTarget = event.relatedTarget instanceof Node ? event.relatedTarget : null;
    const isMovingWithinItem = Boolean(relatedTarget && itemContainer?.contains(relatedTarget));
    const isMovingToOverflowMenu = Boolean(
      relatedTarget instanceof Element && relatedTarget.parentElement?.matches('[slot="overflow"]')
    );

    if (isMovingWithinItem || isMovingToOverflowMenu) {
      this.#cancelClose();
      return;
    }

    this.#scheduleDeactivate(item);
  }

  /** @param {HTMLElement | null} item */
  #scheduleDeactivate(item) {
    if (!item || item !== this.#state.activeItem) return;

    this.#cancelClose();
    const activationId = this.#activationId;

    this.#closeTimer = setTimeout(() => {
      if (activationId !== this.#activationId || item !== this.#state.activeItem) return;

      const itemContainer = item.parentElement;
      const itemHovered = itemContainer?.matches(':hover') ?? false;
      const focusWithinItem = itemContainer?.contains(document.activeElement) ?? false;

      if (itemHovered || focusWithinItem || this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

      this.#closeImmediately(item, activationId);
    }, CLOSE_DELAY);
  }

  #cancelClose = () => {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
  };

  /**
   * Close the active item only when the item and activation token still match.
   * @param {HTMLElement | null} item
   * @param {number} activationId
   * @param {boolean} [force]
   */
  #closeImmediately(item, activationId, force = false) {
    if (!item || item !== this.#state.activeItem || activationId !== this.#activationId) return;
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    const submenu = this.#state.activeSubmenu;

    this.#activationId += 1;
    this.#cancelClose();
    this.#cancelMetricsSync();
    this.#disconnectResizeObserver();

    item.ariaExpanded = 'false';
    if (submenu) {
      delete submenu.dataset.active;
    }

    this.#state = {
      activeItem: null,
      activeSubmenu: null,
      activeControlId: null,
      isDefaultSlot: true,
      hasSubmenu: false,
    };

    this.dataset.overflowExpanded = 'false';
    this.ariaExpanded = 'false';
    this.style.setProperty('--submenu-opacity', '0');
    this.#applyHeaderHeight(0);
  }

  #prepareActiveSubmenu() {
    const { activeItem, activeSubmenu } = this.#state;
    if (!activeItem || !activeSubmenu) return;

    this.dispatchEvent(
      new CustomEvent(PREPARE_EVENT, {
        bubbles: true,
        detail: { item: activeItem, submenu: activeSubmenu },
      })
    );
    this.#preloadImages(activeSubmenu);
  }

  #syncActiveSubmenuMetrics() {
    const { activeItem, activeSubmenu, isDefaultSlot, hasSubmenu } = this.#state;
    if (!activeItem) return;

    let finalHeight = activeSubmenu?.offsetHeight || 0;

    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();

      if (hasSubmenu) {
        finalHeight = Math.max(this.overflowMenu?.offsetHeight || 0, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!activeSubmenu) {
      finalHeight = 0;
    }

    this.#applyHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', finalHeight > 0 ? '1' : '0');
  }

  /**
   * @param {HTMLElement | null} submenu
   * @param {number} activationId
   */
  #observeActiveSubmenu(submenu, activationId) {
    if (!submenu || typeof ResizeObserver === 'undefined') return;

    this.#submenuResizeObserver = new ResizeObserver(() => {
      if (activationId !== this.#activationId || submenu !== this.#state.activeSubmenu) return;
      this.#scheduleMetricsSync(activationId);
    });

    this.#submenuResizeObserver.observe(submenu);

    const submenuInner = submenu.querySelector('.menu-list__submenu-inner');
    if (submenuInner instanceof HTMLElement) {
      this.#submenuResizeObserver.observe(submenuInner);
    }
  }

  /** @param {number} activationId */
  #scheduleMetricsSync(activationId) {
    this.#cancelMetricsSync();
    this.#metricsAnimationFrame = requestAnimationFrame(() => {
      this.#metricsAnimationFrame = undefined;
      if (activationId !== this.#activationId) return;
      this.#syncActiveSubmenuMetrics();
    });
  }

  #cancelMetricsSync() {
    if (this.#metricsAnimationFrame === undefined) return;
    cancelAnimationFrame(this.#metricsAnimationFrame);
    this.#metricsAnimationFrame = undefined;
  }

  #disconnectResizeObserver() {
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }

  #activeStructureNeedsReconcile() {
    const activeItem = this.#resolveActiveItem();
    if (!activeItem) return true;

    const isDefaultSlot = activeItem.parentElement?.slot === '';
    let activeSubmenu = findSubmenu(activeItem);
    const hasSubmenu = Boolean(activeSubmenu);

    if (!hasSubmenu && !isDefaultSlot) {
      activeSubmenu = this.overflowMenu;
    }

    return (
      activeItem !== this.#state.activeItem ||
      activeItem.getAttribute('aria-expanded') !== 'true' ||
      activeSubmenu !== this.#state.activeSubmenu ||
      isDefaultSlot !== this.#state.isDefaultSlot ||
      hasSubmenu !== this.#state.hasSubmenu ||
      Boolean(activeSubmenu && !activeSubmenu.hasAttribute('data-active'))
    );
  }

  #scheduleStructureReconcile() {
    this.#cancelStructureReconcile();
    this.#structureAnimationFrame = requestAnimationFrame(() => {
      this.#structureAnimationFrame = undefined;
      this.#reconcileActiveStructure();
    });
  }

  #cancelStructureReconcile() {
    if (this.#structureAnimationFrame === undefined) return;
    cancelAnimationFrame(this.#structureAnimationFrame);
    this.#structureAnimationFrame = undefined;
  }

  #reconcileActiveStructure() {
    if (!this.#state.activeItem) return;

    const activeItem = this.#resolveActiveItem();
    if (!activeItem || !this.#isDesktopMenu()) {
      this.#closeImmediately(this.#state.activeItem, this.#activationId, true);
      return;
    }

    this.#openItem(activeItem);
  }

  #resolveActiveItem() {
    if (this.#state.activeItem && this.contains(this.#state.activeItem)) {
      return this.#state.activeItem;
    }

    if (!this.#state.activeControlId) return null;

    const activeItem = [...this.querySelectorAll('[ref="menuitem"]')].find(
      (item) => item.getAttribute('aria-controls') === this.#state.activeControlId
    );

    return activeItem instanceof HTMLElement ? activeItem : null;
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /** @param {(submenu: HTMLElement) => void} callback */
    const mapSubmenus = (callback) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) callback(submenu);
      });
    };

    mapSubmenus((submenu) => submenu.style.setProperty('display', 'none'));
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => submenu.style.removeProperty('display'));

    return height;
  }

  /** @param {number} submenuHeight */
  #applyHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;
    const fullOpenHeaderHeight = submenuHeight === 0 ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.headerComponent.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  #isDesktopMenu() {
    return this.headerComponent?.dataset.menuStyle === 'menu' && window.matchMedia('(min-width: 750px)').matches;
  }

  #preloadImages = (root) => {
    const images = root.querySelectorAll('img[loading="lazy"]');
    images.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the direct menu item for an event target.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const listItem = element.closest('.menu-list__list-item');
  if (!listItem) return null;

  if (listItem.matches('[slot="more"]')) {
    return findMenuItem(listItem.parentElement?.querySelector('[slot="overflow"]'));
  }

  const item = listItem.querySelector(':scope > [ref="menuitem"]');
  return item instanceof HTMLElement ? item : null;
}

/**
 * Find the submenu owned by a menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector(':scope > [ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
